import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { SmokeBackground } from '../components/ui/spooky-smoke-animation';
import { 
  FiTarget, 
  FiEye, 
  FiHeart, 
  FiUsers,
  FiShield,
  FiZap
} from 'react-icons/fi';

// ========= EXTRACTED COMPONENTS (Rules of Hooks compliant) =========

const MissionCard = ({ item, index, scrollYProgress }) => {
  const cardY = useTransform(
    scrollYProgress,
    [0.1 + index * 0.05, 0.4 + index * 0.05],
    [40, 0]
  );
  const smoothCardY = useSpring(cardY, { stiffness: 80, damping: 25 });

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: item.delay }}
      style={{ y: smoothCardY }}
      className="text-center p-8 bg-white rounded-2xl shadow-paper border border-gray-100"
    >
      <div className="inline-flex p-4 bg-gray-100 rounded-xl text-gray-900 mb-6">
        {item.icon}
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-4 tracking-tight">{item.title}</h3>
      <p className="text-gray-500 leading-relaxed text-sm">{item.description}</p>
    </motion.div>
  );
};

const ValueCard = ({ value, index, scrollYProgress }) => {
  const direction = index % 2 === 0 ? 1 : -1;
  const cardX = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    [direction * 25, 0, direction * -10]
  );
  const smoothCardX = useSpring(cardX, { stiffness: 80, damping: 25 });

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: index * 0.1 }}
      style={{ x: smoothCardX }}
      className="bg-white p-8 rounded-2xl shadow-paper border border-gray-100 text-center card-hover"
    >
      <div className="inline-flex p-4 bg-gray-100 rounded-xl text-gray-900 mb-6">
        {value.icon}
      </div>
      <h3 className="text-lg font-bold text-gray-900 mb-4 tracking-tight">{value.title}</h3>
      <p className="text-gray-500 text-sm leading-relaxed">{value.description}</p>
    </motion.div>
  );
};

const AboutStatItem = ({ stat, index, scrollYProgress }) => {
  const statScale = useTransform(
    scrollYProgress,
    [0.1 + index * 0.05, 0.4 + index * 0.05],
    [0.8, 1]
  );
  const smoothStatScale = useSpring(statScale, { stiffness: 100, damping: 20 });

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      whileInView={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8, delay: index * 0.1 }}
      style={{ scale: smoothStatScale }}
      className="text-center"
    >
      <div className="text-4xl md:text-5xl font-bold text-white mb-2 tracking-tighter">
        {stat.number}
      </div>
      <div className="text-gray-400 text-xs uppercase tracking-widest">{stat.label}</div>
    </motion.div>
  );
};

const TeamCard = ({ member, index, scrollYProgress }) => {
  const direction = index === 0 ? -1 : 1;
  const cardX = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    [direction * 30, 0, direction * -15]
  );
  const smoothCardX = useSpring(cardX, { stiffness: 80, damping: 25 });

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: index * 0.1 }}
      style={{ x: smoothCardX }}
      className="bg-white p-8 rounded-2xl shadow-paper border border-gray-100 text-center card-hover"
    >
      <div className="w-20 h-20 bg-gray-950 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <span className="text-white font-bold text-xl tracking-tight">
          {member.name.split(' ').map(n => n[0]).join('')}
        </span>
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2 tracking-tight">{member.name}</h3>
      <p className="text-gray-500 font-medium mb-4 text-sm uppercase tracking-widest">{member.role}</p>
      <p className="text-gray-400 mb-6 text-sm leading-relaxed">{member.description}</p>
      <div className="flex flex-wrap gap-2 justify-center">
        {member.skills.map((skill, idx) => (
          <span
            key={idx}
            className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium"
          >
            {skill}
          </span>
        ))}
      </div>
    </motion.div>
  );
};

// ========= MAIN COMPONENT =========

const About = () => {
  const navigate = useNavigate();

  // ========= PARALLAX REFS & SCROLL =========
  const heroRef = useRef(null);
  const missionRef = useRef(null);
  const valuesRef = useRef(null);
  const statsRef = useRef(null);
  const teamRef = useRef(null);
  const ctaRef = useRef(null);

  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroTextY = useTransform(heroProgress, [0, 1], [0, 100]);
  const heroTextOpacity = useTransform(heroProgress, [0, 0.6], [1, 0]);
  const heroScale = useTransform(heroProgress, [0, 0.5], [1, 0.97]);

  const { scrollYProgress: missionProgress } = useScroll({
    target: missionRef,
    offset: ["start end", "end start"],
  });

  const { scrollYProgress: valuesProgress } = useScroll({
    target: valuesRef,
    offset: ["start end", "end start"],
  });

  const { scrollYProgress: statsProgress } = useScroll({
    target: statsRef,
    offset: ["start end", "end start"],
  });
  const statsBgY = useTransform(statsProgress, [0, 1], [40, -40]);
  const smoothStatsBgY = useSpring(statsBgY, { stiffness: 80, damping: 25 });

  const { scrollYProgress: teamProgress } = useScroll({
    target: teamRef,
    offset: ["start end", "end start"],
  });

  const { scrollYProgress: ctaProgress } = useScroll({
    target: ctaRef,
    offset: ["start end", "end start"],
  });
  const ctaBgY = useTransform(ctaProgress, [0, 1], [30, -30]);
  const smoothCtaBgY = useSpring(ctaBgY, { stiffness: 80, damping: 25 });

  const missionItems = [
    {
      icon: <FiTarget className="w-7 h-7" />,
      title: "Nossa Missão",
      description: "Democratizar o acesso à tecnologia avançada, oferecendo soluções inovadoras que transformam pequenos e médios negócios em empresas mais eficientes e competitivas.",
      delay: 0,
    },
    {
      icon: <FiEye className="w-7 h-7" />,
      title: "Nossa Visão",
      description: "Ser referência nacional em soluções tecnológicas personalizadas, reconhecida pela excelência em automação, inteligência artificial e inovação disruptiva.",
      delay: 0.2,
    },
    {
      icon: <FiHeart className="w-7 h-7" />,
      title: "Nossos Valores",
      description: "Paixão pela tecnologia, compromisso com a excelência, foco no cliente, inovação constante e transparência em todas as nossas relações e processos.",
      delay: 0.4,
    },
  ];

  const values = [
    {
      icon: <FiZap className="w-7 h-7" />,
      title: "Inovação",
      description: "Sempre buscamos as tecnologias mais avançadas para criar soluções revolucionárias."
    },
    {
      icon: <FiShield className="w-7 h-7" />,
      title: "Confiabilidade",
      description: "Construímos sistemas robustos e seguros que nossos clientes podem confiar."
    },
    {
      icon: <FiHeart className="w-7 h-7" />,
      title: "Paixão",
      description: "Amamos o que fazemos e isso se reflete na qualidade de cada projeto."
    },
    {
      icon: <FiUsers className="w-7 h-7" />,
      title: "Colaboração",
      description: "Trabalhamos em parceria com nossos clientes para alcançar os melhores resultados."
    }
  ];

  const team = [
    {
      name: "Vitor Ayres",
      role: "Fundador e Desenvolvedor Principal",
      description: "Especialista no desenvolvimento e automação.",
      skills: ["Inteligência Artificial", "React", "Flask", "Python", "Node.js", "Docker", "SQL"]
    },
    {
      name: "Junior Fernandes",
      role: "Co-Fundador, Vendas e Marketing",
      description: "Arquiteta de soluções e especialista em desenvolvimento full-stack.",
      skills: ["Vendas", "Marketing", "Desenvolvimento Full-Stack", "Gestão de Projetos"]
    }
  ];

  const stats = [
    { number: "2025", label: "Fundada em" },
    { number: "10+", label: "Projetos Entregues"},
    { number: "3+", label: "Anos de Experiência"},
    { number: "99%", label: "Satisfação do Cliente" }
  ];

  return (
    <div className="min-h-screen">

      {/* Hero Section */}
      <section ref={heroRef} className="pt-44 pb-44 parallax-section bg-gray-950" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="pointer-events-none absolute inset-0 z-0" aria-hidden="true">
          <SmokeBackground smokeColor="#2f2f2f" />
        </div>
        <div className="pointer-events-none absolute inset-0 z-0 parallax-grid opacity-20" aria-hidden="true" />

        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10"
          style={{
            y: heroTextY,
            opacity: heroTextOpacity,
            scale: heroScale,
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
          >
            <span className="text-xs text-white/70 uppercase tracking-widest font-mono">Quem somos</span>
            <h1 className="text-5xl md:text-7xl font-display font-bold text-white mt-4 mb-6 tracking-tighter leading-tight">
              Sobre a AyTech
            </h1>
            <p className="text-lg text-white/70 max-w-2xl leading-relaxed">
              Uma empresa nascida da paixão pela tecnologia e pelo desejo de transformar 
              negócios através de soluções inovadoras e inteligentes.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Mission, Vision, Values */}
      <section ref={missionRef} className="py-24 bg-gray-50 parallax-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-14"
          >
            <span className="text-xs text-gray-400 uppercase tracking-widest font-mono">Propósito</span>
            <h2 className="text-4xl font-display font-bold text-gray-950 mt-3 tracking-tighter" style={{ color: '#0a0a0a' }}>
              Missão, Visão e Valores
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {missionItems.map((item, index) => (
              <MissionCard
                key={index}
                item={item}
                index={index}
                scrollYProgress={missionProgress}
              />
            ))}
          </div>
        </div>

        <div className="glow-line max-w-4xl mx-auto mt-24" />
      </section>

      {/* Values Detail */}
      <section ref={valuesRef} className="py-24 bg-white parallax-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-14"
          >
            <span className="text-xs text-gray-400 uppercase tracking-widest font-mono">Princípios</span>
            <h2 className="text-4xl font-display font-bold text-gray-950 mt-3 tracking-tighter" style={{ color: '#0a0a0a' }}>
              Nossos Valores
            </h2>
            <p className="text-gray-500 mt-4 max-w-xl text-base leading-relaxed">
              Os princípios que guiam nossa empresa e moldam nossa cultura
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <ValueCard
                key={index}
                value={value}
                index={index}
                scrollYProgress={valuesProgress}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section ref={statsRef} className="py-24 parallax-section bg-gray-950" style={{ backgroundColor: '#0a0a0a' }}>
        <motion.div
          className="absolute inset-0 parallax-dots opacity-30"
          style={{ y: smoothStatsBgY }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-14"
          >
            <span className="text-xs text-gray-500 uppercase tracking-widest font-mono">Números</span>
            <h2 className="text-3xl font-display font-bold text-white mt-3 tracking-tight">
              AyTech em números
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <AboutStatItem
                key={index}
                stat={stat}
                index={index}
                scrollYProgress={statsProgress}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section ref={teamRef} className="py-24 bg-gray-50 parallax-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-14"
          >
            <span className="text-xs text-gray-400 uppercase tracking-widest font-mono">Equipe</span>
            <h2 className="text-4xl font-display font-bold text-gray-950 mt-3 tracking-tighter" style={{ color: '#0a0a0a' }}>
              Nossa Equipe
            </h2>
            <p className="text-gray-500 mt-4 max-w-xl text-base leading-relaxed">
              Profissionais apaixonados por tecnologia e comprometidos com a excelência
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {team.map((member, index) => (
              <TeamCard
                key={index}
                member={member}
                index={index}
                scrollYProgress={teamProgress}
              />
            ))}
          </div>
        </div>

        <div className="glow-line max-w-4xl mx-auto mt-24" />
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="py-24 parallax-section bg-gray-950" style={{ backgroundColor: '#0a0a0a' }}>
        <motion.div
          className="absolute inset-0 parallax-grid opacity-15"
          style={{ y: smoothCtaBgY }}
        />

        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-xs text-gray-500 uppercase tracking-widest font-mono">Próximo passo</span>
            <h2 className="text-4xl font-display font-bold text-white mt-4 mb-6 tracking-tighter">
              Está pronto para revolucionar seu negócio?
            </h2>
            <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
              Entre em contato conosco e descubra como podemos transformar seu negócio juntos.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => navigate('/contact')}
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-950 font-semibold text-sm uppercase tracking-widest rounded-full transition-all duration-300 hover:bg-gray-100 btn-hover"
                style={{ color: '#0a0a0a' }}
              >
                Fale Conosco
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
